/**
 * SEO Component — Restore.Dental
 * 
 * Single source of truth for ALL meta tags across the entire site.
 * Every page MUST render exactly ONE <SEO /> component at the top.
 * 
 * NO inline <meta> tags allowed anywhere else in the codebase.
 * 
 * Uses React 19's native document metadata hoisting - no Helmet needed!
 * 
 * @component
 * @example
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   path="/page-url"
 *   image="/custom-og-image.jpg"
 *   keywords={["keyword1", "keyword2"]}
 *   noindex={false}
 *   structuredData={[organizationSchema, localBusinessSchema]}
 * />
 */

import React from 'react';
import { 
  PUBLIC_SITE_URL, 
  DEFAULT_OG_IMAGE,
  SITE_DEFAULTS,
  getCanonicalURL,
  getOGImage,
} from '@/data/seo';
import { PRACTICE } from '@/data/practice';

const SEO = ({
  title,
  description,
  path = '/',
  image,
  keywords = [],
  noindex = false,
  structuredData = [],
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
}) => {
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const canonicalURL = getCanonicalURL(path);
  const ogImageURL = getOGImage(image);
  const keywordsString = Array.isArray(keywords) ? keywords.join(', ') : (keywords || '');

  // Ensure title is always a valid string
  const pageTitle = title ? String(title).trim() : SITE_DEFAULTS.siteName;
  
  // Ensure description is always a valid string
  const pageDescription = description ? String(description).trim() : 
    'Expert dental care in West London. Invisalign, implants, cosmetic dentistry. Award-winning practice with 15+ years experience.';

  // ============================================================================
  // STRUCTURED DATA RENDERING
  // ============================================================================

  const renderStructuredData = () => {
    if (!structuredData || structuredData.length === 0) return null;

    // Filter out null/undefined schemas
    const validSchemas = structuredData.filter(data => data !== null && data !== undefined);
    
    if (validSchemas.length === 0) return null;

    return validSchemas.map((data, index) => (
      <script
        key={`structured-data-${index}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(data, null, 0), // Minified for production
        }}
      />
    ));
  };

  // ============================================================================
  // REACT 19 NATIVE DOCUMENT METADATA
  // React 19 automatically hoists these tags to <head>
  // ============================================================================

  return (
    <>
      {/* ===== PRIMARY META TAGS ===== */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      
      {/* ===== CANONICAL URL + HREFLANG ===== */}
      <link rel="canonical" href={canonicalURL} />
      <link rel="alternate" hrefLang="en-gb" href={canonicalURL} />
      <link rel="alternate" hrefLang="x-default" href={canonicalURL} />

      {/* ===== ROBOTS META ===== */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* ===== OPEN GRAPH (Facebook, LinkedIn, WhatsApp) ===== */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalURL} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImageURL} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:site_name" content={SITE_DEFAULTS.siteName} />
      <meta property="og:locale" content={SITE_DEFAULTS.locale} />

      {/* ===== TWITTER CARD ===== */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalURL} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImageURL} />
      <meta name="twitter:image:alt" content={pageTitle} />
      {SITE_DEFAULTS.twitterHandle && (
        <meta name="twitter:site" content={SITE_DEFAULTS.twitterHandle} />
      )}

      {/* ===== ARTICLE METADATA ===== */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === 'article' && author && <meta name="author" content={author} />}

      {/* ===== ADDITIONAL METADATA ===== */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="GB-EAL" />
      <meta name="geo.placename" content="Acton, London" />
      <meta name="geo.position" content={`${PRACTICE.geo.lat};${PRACTICE.geo.lng}`} />
      <meta name="ICBM" content={`${PRACTICE.geo.lat}, ${PRACTICE.geo.lng}`} />

      {/* ===== STRUCTURED DATA (JSON-LD) ===== */}
      {renderStructuredData()}
    </>
  );
};

// ============================================================================
// PROP TYPES (for development validation)
// ============================================================================

if (process.env.NODE_ENV === 'development') {
  SEO.propTypes = {
    title: (props, propName, componentName) => {
      if (props[propName] && props[propName].length > 65) {
        return new Error(
          `${componentName}: title "${props[propName]}" is ${props[propName].length} characters (max 65)`
        );
      }
    },
    description: (props, propName, componentName) => {
      if (props[propName] && props[propName].length > 160) {
        return new Error(
          `${componentName}: description is ${props[propName].length} characters (max 160)`
        );
      }
    },
  };
}

export default SEO;
